import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, MessageSquare, Users, DollarSign, Printer, Sparkles, Flower2, Truck, Timer } from "lucide-react";
import { api } from "../../lib/api";
import { PageTitle, StatusBadge } from "./shared";

const StatCard = ({ Icon, label, value, color, testid }) => (
  <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-5 flex items-center gap-4" data-testid={testid}>
    <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </span>
    <div>
      <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold">{label}</p>
      <p className="font-serif-display text-3xl text-plum-deep leading-none mt-1">{value}</p>
    </div>
  </div>
);

const ConsoleDashboard = () => {
  const [data, setData] = useState(null);
  const [prod, setProd] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    api.get("/admin/production-dashboard").then((r) => setProd(r.data)).catch(() => setProd({}));
  }, []);

  if (!data) return <p className="text-plum-deep/60">Loading dashboard…</p>;

  const queueCount = (prod?.print_status_counts?.queued || 0) + (prod?.print_status_counts?.printing || 0);

  return (
    <div data-testid="console-dashboard">
      <PageTitle kicker="SHARED ADMIN · OVERVIEW" title="DASHBOARD" />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard Icon={ShoppingBag} label="Total orders" value={data.total_orders ?? 0} color="#EC2675" testid="stat-total-orders" />
        <StatCard Icon={Sparkles} label="New orders" value={data.new_orders ?? 0} color="#FF7A3D" testid="stat-new-orders" />
        <StatCard Icon={Flower2} label="Heritage waiting" value={prod?.heritage_waiting ?? 0} color="#1F8A50" testid="stat-heritage-waiting" />
        <StatCard Icon={MessageSquare} label="New enquiries" value={data.new_enquiries ?? 0} color="#4ECDE9" testid="stat-new-enquiries" />
        <StatCard Icon={Users} label="Customers" value={data.customers ?? 0} color="#6E2470" testid="stat-customers" />
        <StatCard Icon={DollarSign} label="Revenue (NZD)" value={`$${(data.revenue ?? 0).toFixed(0)}`} color="#C61F62" testid="stat-revenue" />
      </div>

      <div className="mt-6 bg-plum text-cream rounded-[1.5rem] p-6" data-testid="production-today-panel">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif-display uppercase text-xl tracking-[0.06em]">PRODUCTION TODAY</h2>
          <Link to="/console/print-queue" className="text-lemon text-xs uppercase tracking-[0.16em] font-semibold hover:underline">OPEN PRINT QUEUE</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { Icon: Printer, label: "Tags printed today", value: prod?.tags_printed_today ?? "—", testid: "prod-tags-today" },
            { Icon: Sparkles, label: "Jobs in queue", value: queueCount, testid: "prod-queue" },
            { Icon: ShoppingBag, label: "Orders waiting", value: prod?.orders_waiting ?? "—", testid: "prod-waiting" },
            { Icon: DollarSign, label: "Revenue today", value: `$${(prod?.revenue_today ?? 0).toFixed(0)}`, testid: "prod-revenue" },
            { Icon: Truck, label: "Shipped today", value: prod?.shipped_today ?? "—", testid: "prod-shipped" },
          ].map(({ Icon, label, value, testid }) => (
            <div key={label} className="bg-cream/10 rounded-2xl p-4" data-testid={testid}>
              <Icon className="w-4 h-4 text-lemon" />
              <p className="font-serif-display text-3xl mt-2 leading-none">{value}</p>
              <p className="text-[10px] uppercase tracking-[0.16em] text-cream/70 font-semibold mt-1.5">{label}</p>
            </div>
          ))}
        </div>
        {prod?.avg_turnaround_days != null && (
          <p className="mt-4 flex items-center gap-2 text-xs text-cream/70 uppercase tracking-[0.14em]">
            <Timer className="w-3.5 h-3.5 text-lemon" /> AVERAGE TURNAROUND: {prod.avg_turnaround_days} DAYS
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6" data-testid="recent-orders-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display uppercase text-plum-deep text-xl tracking-[0.06em]">RECENT ORDERS</h2>
            <Link to="/console/orders" className="text-magenta text-xs uppercase tracking-[0.16em] font-semibold hover:underline">VIEW ALL</Link>
          </div>
          {(data.recent_orders || []).length === 0 ? (
            <p className="text-sm text-plum-deep/60">No orders yet. They'll appear here when customers order from the Foundry Workshop.</p>
          ) : (
            <ul className="divide-y divide-plum-deep/10">
              {data.recent_orders.map((o) => (
                <li key={o.id} className="py-3 flex items-center gap-4">
                  <span className="font-semibold text-sm w-20 shrink-0">{o.order_number}</span>
                  <span className="flex-1 text-sm truncate">{o.customer?.name}</span>
                  <span className="text-sm text-plum-deep/70">${o.total?.toFixed(2)}</span>
                  <StatusBadge status={o.status} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6" data-testid="recent-enquiries-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display uppercase text-plum-deep text-xl tracking-[0.06em]">ENQUIRIES</h2>
            <Link to="/console/enquiries" className="text-magenta text-xs uppercase tracking-[0.16em] font-semibold hover:underline">VIEW ALL</Link>
          </div>
          {(data.recent_enquiries || []).length === 0 ? (
            <p className="text-sm text-plum-deep/60">No enquiries yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.recent_enquiries.map((e) => (
                <li key={e.id} className="bg-cream rounded-2xl p-3">
                  <p className="text-sm font-semibold">{e.name}</p>
                  <p className="text-xs text-plum-deep/60 truncate">{e.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsoleDashboard;
